// Country / academic-region options for course creation.
//
// Single source of truth for the country dropdown. To support a new region
// (Canada, UK, Australia, ...) add ONE entry here - no other UI code changes.
// Codes are ISO 3166-1 alpha-2 and must match the backend country registry
// (backend/app/constants/countries.py). The backend GET /courses/supported-countries
// endpoint returns this same list, so this can later be replaced by a fetch.

// assessmentUnit / assessmentUnitAbbr / currency* mirror the backend
// CountryProfile conventions (backend/app/constants/countries.py). They drive
// display only ("Marks"/"M" vs "Points"/"pts"; "₹"/INR vs "$"/USD); IN keeps
// today's wording and currency so India display is byte-identical.
// `sessionLabel` names the exam-edition axis: India groups papers by academic
// regulation (R20, R23, ...), the US by academic season (Spring/Fall, ...).
export const SUPPORTED_COUNTRIES = [
  { code: 'IN', name: 'India', assessmentUnit: 'Marks', assessmentUnitAbbr: 'M', sessionLabel: 'Regulation',
    currencyCode: 'INR', currencySymbol: '₹', currencyLocale: 'en-IN', currencyName: 'Indian Rupees' },
  { code: 'US', name: 'United States', assessmentUnit: 'Points', assessmentUnitAbbr: 'pts', sessionLabel: 'Season',
    currencyCode: 'USD', currencySymbol: '$', currencyLocale: 'en-US', currencyName: 'US Dollars' },
];

// Mirror of backend DEFAULT_COUNTRY_CODE. Used only as a pre-auth / session-
// restore fallback. Once authenticated, the backend user profile's
// country_code is the single source of truth.
export const DEFAULT_COUNTRY_CODE = 'IN';

const COUNTRY_BY_CODE = SUPPORTED_COUNTRIES.reduce((map, c) => {
  map[c.code] = c;
  return map;
}, {});

// Resolve a raw/undefined code to a supported country, defaulting to IN. Mirrors
// the backend resolve_country_code(): None / unknown / legacy all fall to IN.
export function resolveCountry(code) {
  if (!code) return COUNTRY_BY_CODE[DEFAULT_COUNTRY_CODE];
  return COUNTRY_BY_CODE[String(code).toUpperCase()] || COUNTRY_BY_CODE[DEFAULT_COUNTRY_CODE];
}

// Long-form assessment terminology for display ("Marks" / "Points").
export function getAssessmentUnit(code) {
  return resolveCountry(code).assessmentUnit;
}

// Short badge terminology for display ("M" / "pts").
export function getAssessmentUnitAbbr(code) {
  return resolveCountry(code).assessmentUnitAbbr;
}

// Exam-edition axis label for the current region ("Regulation" / "Season").
export function getSessionLabel(code) {
  return resolveCountry(code).sessionLabel;
}

// Lowercase singular/plural assessment word for inline text ("5 marks" / "1 mark",
// "5 points" / "1 point"). For IN this yields exactly "mark"/"marks", so existing
// India copy stays byte-identical.
export function getAssessmentWord(code, count) {
  const singular = getAssessmentUnit(code).replace(/s$/i, '').toLowerCase();
  return count === 1 ? singular : `${singular}s`;
}

// Currency symbol for display ("₹" / "$"). Derived from a course's country_code
// on the fly - never stored per course. IN resolves to "₹".
export function getCurrencySymbol(code) {
  return resolveCountry(code).currencySymbol;
}

// ISO 4217 currency code ("INR" / "USD"). Used for the payment/Razorpay flow and
// Intl currency formatting. IN resolves to "INR".
export function getCurrencyCode(code) {
  return resolveCountry(code).currencyCode;
}

// BCP-47 locale used for price number grouping ("en-IN" / "en-US"). IN resolves
// to "en-IN", so India price grouping stays byte-identical.
export function getCurrencyLocale(code) {
  return resolveCountry(code).currencyLocale;
}

// Full currency name for prose/labels ("Indian Rupees" / "US Dollars"). IN
// resolves to "Indian Rupees", so India copy stays byte-identical.
export function getCurrencyName(code) {
  return resolveCountry(code).currencyName;
}

// Format a course price with its region's currency, e.g. "₹1,234" for IN or
// "$1,234" for US. Mirrors the dominant `symbol + toLocaleString(locale)` pattern
// used across the app; for IN it yields exactly "₹" + en-IN grouping, so existing
// India price displays are byte-identical. Pass the course's country_code.
export function formatPrice(amount, code) {
  const country = resolveCountry(code);
  return `${country.currencySymbol}${Number(amount || 0).toLocaleString(country.currencyLocale)}`;
}

// Bare currency symbol for an ISO 4217 code ("INR" -> "₹", "USD" -> "$"). For
// places that keep their own number formatting (e.g. receipts that show 2
// decimals) and only need to swap the symbol. Unknown code falls back to itself.
export function getCurrencySymbolByCode(currencyCode) {
  const code = (currencyCode || "INR").toUpperCase();
  const country = SUPPORTED_COUNTRIES.find((c) => c.currencyCode === code);
  return country ? country.currencySymbol : code;
}

// Format an amount given an ISO 4217 currency code (e.g. from a stored payment
// record), reusing the matching region's symbol + locale. Use this where the
// currency is known but the country_code is not (payment receipts/history). An
// unknown code falls back to prefixing the raw code. "INR" stays "₹...,en-IN".
export function formatAmountByCurrency(amount, currencyCode) {
  const code = (currencyCode || "INR").toUpperCase();
  const country = SUPPORTED_COUNTRIES.find((c) => c.currencyCode === code);
  const symbol = country ? country.currencySymbol : `${code} `;
  const locale = country ? country.currencyLocale : "en-IN";
  return `${symbol}${Number(amount || 0).toLocaleString(locale)}`;
}

export default SUPPORTED_COUNTRIES;
